import { provide, inject, type InjectionKey } from 'vue'
import { useDocumentState } from './useDocumentState'
import { useDragAndDrop } from './useDragAndDrop'
import { usePdfOperations } from './usePdfOperations'
import { useDocumentOperations } from './useDocumentOperations'
import { useApiOperations } from './useApiOperations'
import type { DocumentGroup } from './useDocumentState'

// Combined context interface
interface DocumentOrganizationContext {
  // All methods and state from individual composables
  [key: string]: any
}

// Create the injection key
const DocumentOrganizationContextKey: InjectionKey<DocumentOrganizationContext> = Symbol(
  'DocumentOrganizationContext'
)

/**
 * Main composable that combines all document organization functionality
 * This replaces the original massive useDocumentOrganization composable
 */
export const useDocumentOrganization = (
  t?: (key: string, params?: Record<string, any>) => string,
  options: { asProvider?: boolean; additionalContext?: Record<string, any> } = {}
) => {
  // Initialize document state
  const documentState = useDocumentState({ asProvider: options.asProvider })

  // Initialize other composables directly with the document state passed in
  let dragAndDrop: any = {}
  let pdfOperations: any = {}
  let documentOperations: any = {}
  let apiOperations: any = {}

  if (t) {
    // Pass document state directly to avoid context dependency
    dragAndDrop = useDragAndDrop(t, documentState)
    pdfOperations = usePdfOperations(t)
    documentOperations = useDocumentOperations(t, documentState)
    apiOperations = useApiOperations(t, documentState)
  }

  // Combine all functionality
  const combinedContext = {
    // Document state
    ...documentState,

    // Drag and drop (if available)
    ...dragAndDrop,

    // PDF operations (if available)
    ...pdfOperations,

    // Document operations (if available)
    ...documentOperations,

    // API operations (if available)
    ...apiOperations,

    // Additional context functions (if provided)
    ...(options.additionalContext || {})
  }

  // Provide the combined context
  if (options.asProvider) {
    provide(DocumentOrganizationContextKey, combinedContext)
  }

  return combinedContext
}

// Consumer hook for inject
export const useDocumentOrganizationContext = () => {
  const context = inject(DocumentOrganizationContextKey)

  if (!context) {
    throw new Error(
      'useDocumentOrganizationContext must be used within a DocumentOrganizationProvider'
    )
  }

  return context
}

// Export types for convenience
export type { DocumentGroup } from './useDocumentState'
export type { PageItem } from './useDocumentState'
